import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { AuthImage } from "@/components/AuthImage";
import { ImageZoomModal } from "@/components/ImageZoomModal";
import { toast } from "sonner";
import { X, Pencil, Image as ImageIcon, RotateCcw, AlertTriangle, Clock, Youtube, Plus } from "lucide-react";

export default function Gradebook() {
  const query = new URLSearchParams(useLocation().search);
  const studentParam = query.get("student");
  const [students, setStudents] = useState([]);
  const [filter, setFilter] = useState(studentParam || "");
  const [subs, setSubs] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [watched, setWatched] = useState([]);
  const [active, setActive] = useState(null);
  const [resetSub, setResetSub] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    const q = filter ? `?student_id=${filter}` : "";
    api.get(`/submissions${q}`).then((r) => setSubs(r.data));
    api.get(`/drafts${q}`).then((r) => setDrafts(r.data)).catch(() => setDrafts([]));
    api.get(`/video/watched${q}`).then((r) => setWatched(r.data)).catch(() => setWatched([]));
  };
  useEffect(() => { api.get("/students").then((r) => setStudents(r.data)); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const nameOf = (id) => students.find((s) => s.id === id)?.first_name || "—";

  const doReset = async () => {
    if (!resetSub) return;
    setResetting(true);
    try {
      await api.delete(`/submissions/${resetSub.id}`);
      toast.success("Assignment reset and removed from the gradebook.");
      setResetSub(null);
      if (active && active.id === resetSub.id) setActive(null);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setResetting(false); }
  };

  return (
    <div>
      <TopBar subtitle="Gradebook" />
      <main className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <h1 className="font-head text-4xl font-bold text-[#3D405B]">Gradebook</h1>
          <div className="flex items-center gap-3">
            <button data-testid="add-grade-entry-btn" onClick={() => setShowAdd(true)}
              className="flex items-center gap-2 rounded-full bg-[#81B29A] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#6a9a83] transition-colors">
              <Plus size={16} /> Add grade entry
            </button>
            <select data-testid="gradebook-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
              className="rounded-full border border-[#EAE6DF] bg-white px-5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E07A5F]">
              <option value="">All students</option>
              {students.map((s) => <option key={s.id} value={s.id}>{s.first_name}</option>)}
            </select>
          </div>
        </div>

        {drafts.length > 0 && (
          <div data-testid="in-progress-panel" className="mb-6 rounded-2xl border border-[#A8DADC]/50 bg-[#A8DADC]/10 p-5">
            <p className="font-head font-semibold text-[#3D405B] mb-3 flex items-center gap-2">
              <Clock size={16} className="text-[#457B9D]" /> In progress
              <span className="text-xs font-normal text-[#6B7280]">— started but not submitted yet</span>
            </p>
            <div className="space-y-2">
              {drafts.map((d) => (
                <div key={d.assignment_id} data-testid={`draft-row-${d.assignment_id}`}
                  className="flex items-center justify-between gap-3 rounded-xl bg-white border border-[#EAE6DF] px-4 py-2.5 text-sm">
                  <span className="text-[#3D405B]"><b>{nameOf(d.student_id)}</b> · {d.subject} — {d.assignment_title}</span>
                  <span className="flex items-center gap-3 text-[#6B7280]">
                    <span className="text-xs">{Object.keys(d.answers || {}).length} answer{Object.keys(d.answers || {}).length === 1 ? "" : "s"} saved</span>
                    <Status s="In Progress" />
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {watched.length > 0 && (
          <div data-testid="videos-watched-panel" className="mb-6 rounded-2xl border border-[#E07A5F]/40 bg-[#E07A5F]/5 p-5">
            <p className="font-head font-semibold text-[#3D405B] mb-3 flex items-center gap-2">
              <Youtube size={16} className="text-[#E07A5F]" /> Videos watched
              <span className="text-xs font-normal text-[#6B7280]">— help videos your kids opened</span>
            </p>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {watched.map((w) => (
                <a key={w.id} data-testid={`watched-row-${w.id}`} href={w.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-between gap-3 rounded-xl bg-white border border-[#EAE6DF] px-4 py-2.5 text-sm hover:border-[#E07A5F] transition-colors">
                  <span className="text-[#3D405B] truncate"><b>{w.student_name || nameOf(w.student_id)}</b> watched "{w.title}"</span>
                  <span className="flex items-center gap-3 text-[#6B7280] shrink-0 whitespace-nowrap">
                    <span className="text-xs">{w.channel}</span>
                    <span className="stat text-xs">{(w.watched_at || "").slice(0, 10)}</span>
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-2xl bg-white border border-[#EAE6DF] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F2EFE9] text-[#6B7280]">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Date</th>
                <th className="text-left px-5 py-3 font-medium">Student</th>
                <th className="text-left px-5 py-3 font-medium">Subject</th>
                <th className="text-left px-5 py-3 font-medium">Assignment</th>
                <th className="text-left px-5 py-3 font-medium">Score</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {subs.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-[#6B7280]">No graded work yet.</td></tr>}
              {subs.map((s) => (
                <tr key={s.id} data-testid={`grade-row-${s.id}`} className="border-t border-[#EAE6DF] hover:bg-[#F7F5F0] transition-colors">
                  <td className="px-5 py-3 text-[#6B7280] stat text-xs">{(s.date_submitted || "").slice(0, 10)}</td>
                  <td className="px-5 py-3 text-[#3D405B]">{nameOf(s.student_id)}</td>
                  <td className="px-5 py-3">{s.subject}</td>
                  <td className="px-5 py-3 text-[#3D405B]">{s.assignment_title}</td>
                  <td className="px-5 py-3"><span className="stat font-semibold text-[#3D405B]">{s.percentage}%</span> <span className="text-[#6B7280]">({s.letter_grade})</span></td>
                  <td className="px-5 py-3"><Status s={s.status} /></td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button data-testid={`view-sub-${s.id}`} onClick={() => setActive(s)} className="text-[#E07A5F] hover:underline">Review</button>
                      <button data-testid={`reset-sub-${s.id}`} onClick={() => setResetSub(s)} title="Reset assignment & remove grade"
                        className="text-[#6B7280] hover:text-[#E07A5F] transition-colors"><RotateCcw size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {active && <SubmissionDrawer sub={active} onClose={() => setActive(null)} onUpdated={(u) => { setActive(u); load(); }} onReset={(s) => setResetSub(s)} />}
      {showAdd && <AddGradeModal students={students} defaultStudent={filter} subjectHints={[...new Set(subs.map((s) => s.subject))]}
        onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {resetSub && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-6 bg-[#3D405B]/60 backdrop-blur-sm" onClick={() => !resetting && setResetSub(null)}>
          <div data-testid="reset-sub-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-white p-8">
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#E07A5F]/15 text-[#E07A5F] mb-4"><AlertTriangle size={22} /></span>
            <h2 className="font-head text-2xl font-bold text-[#3D405B]">Reset this assignment?</h2>
            <p className="text-[#6B7280] mt-2 leading-relaxed">This removes <b>{nameOf(resetSub.student_id)}</b>'s grade for <b>{resetSub.assignment_title}</b> from the gradebook and sets the assignment back to <b>Not Started</b> so it can be done again. The lesson is kept. This can't be undone.</p>
            <div className="flex gap-3 mt-6">
              <button data-testid="reset-sub-cancel" onClick={() => setResetSub(null)} disabled={resetting}
                className="flex-1 rounded-full border border-[#EAE6DF] py-2.5 font-semibold text-[#3D405B] hover:border-[#3D405B] transition-colors disabled:opacity-60">Cancel</button>
              <button data-testid="reset-sub-confirm" onClick={doReset} disabled={resetting}
                className="flex-1 rounded-full bg-[#E07A5F] text-white py-2.5 font-semibold hover:bg-[#c96851] transition-colors disabled:opacity-60">{resetting ? "Resetting…" : "Reset now"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddGradeModal({ students, defaultStudent, subjectHints, onClose, onSaved }) {
  const [f, setF] = useState({
    student_id: defaultStudent || (students[0]?.id || ""),
    subject: "", assignment_title: "", points_earned: "", max_points: "100",
    date_submitted: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.student_id) return toast.error("Choose a student.");
    if (!f.subject.trim() || !f.assignment_title.trim()) return toast.error("Subject and assignment title are required.");
    if (!(Number(f.max_points) > 0)) return toast.error("Max points must be greater than zero.");
    if (f.points_earned === "" || Number(f.points_earned) < 0) return toast.error("Enter the points earned.");
    setSaving(true);
    try {
      await api.post("/submissions/manual", {
        student_id: f.student_id, subject: f.subject.trim(), assignment_title: f.assignment_title.trim(),
        points_earned: Number(f.points_earned), max_points: Number(f.max_points),
        date_submitted: f.date_submitted || undefined,
      });
      toast.success("Grade entry added to the gradebook.");
      onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const pct = Number(f.max_points) > 0 && f.points_earned !== "" ? Math.round((Number(f.points_earned) / Number(f.max_points)) * 1000) / 10 : null;

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-6 bg-[#3D405B]/60 backdrop-blur-sm" onClick={() => !saving && onClose()}>
      <div data-testid="add-grade-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-white p-8">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-head text-2xl font-bold text-[#3D405B]">Add a grade entry</h2>
          <button onClick={() => !saving && onClose()} className="text-[#6B7280] hover:text-[#3D405B]"><X size={20} /></button>
        </div>
        <p className="text-[#6B7280] text-sm mb-5">Record an offline assignment (any subject) straight into the gradebook. A new subject is created automatically if it doesn't exist yet.</p>

        <label className="block text-xs font-semibold text-[#6B7280] mb-1">Student</label>
        <select data-testid="add-grade-student" value={f.student_id} onChange={(e) => set("student_id", e.target.value)}
          className="w-full rounded-xl border border-[#EAE6DF] px-3 py-2.5 mb-4 outline-none focus:ring-2 focus:ring-[#81B29A]">
          {students.map((s) => <option key={s.id} value={s.id}>{s.first_name}</option>)}
        </select>

        <label className="block text-xs font-semibold text-[#6B7280] mb-1">Subject</label>
        <input data-testid="add-grade-subject" list="subject-hints" value={f.subject} onChange={(e) => set("subject", e.target.value)}
          placeholder="e.g. Art, Science, PE" className="w-full rounded-xl border border-[#EAE6DF] px-3 py-2.5 mb-4 outline-none focus:ring-2 focus:ring-[#81B29A]" />
        <datalist id="subject-hints">{(subjectHints || []).map((s) => <option key={s} value={s} />)}</datalist>

        <label className="block text-xs font-semibold text-[#6B7280] mb-1">Assignment title</label>
        <input data-testid="add-grade-title" value={f.assignment_title} onChange={(e) => set("assignment_title", e.target.value)}
          placeholder="e.g. Watercolor project" className="w-full rounded-xl border border-[#EAE6DF] px-3 py-2.5 mb-4 outline-none focus:ring-2 focus:ring-[#81B29A]" />

        <div className="flex gap-3 mb-4">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-[#6B7280] mb-1">Points earned</label>
            <input data-testid="add-grade-earned" type="number" value={f.points_earned} onChange={(e) => set("points_earned", e.target.value)}
              className="w-full rounded-xl border border-[#EAE6DF] px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#81B29A]" />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-semibold text-[#6B7280] mb-1">Out of</label>
            <input data-testid="add-grade-max" type="number" value={f.max_points} onChange={(e) => set("max_points", e.target.value)}
              className="w-full rounded-xl border border-[#EAE6DF] px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#81B29A]" />
          </div>
        </div>

        <label className="block text-xs font-semibold text-[#6B7280] mb-1">Date</label>
        <input data-testid="add-grade-date" type="date" value={f.date_submitted} onChange={(e) => set("date_submitted", e.target.value)}
          className="w-full rounded-xl border border-[#EAE6DF] px-3 py-2.5 mb-4 outline-none focus:ring-2 focus:ring-[#81B29A]" />

        {pct !== null && <p className="text-sm text-[#6B7280] mb-4">Score: <b className="text-[#3D405B]">{pct}%</b></p>}

        <div className="flex gap-3">
          <button data-testid="add-grade-cancel" onClick={() => onClose()} disabled={saving}
            className="flex-1 rounded-full border border-[#EAE6DF] py-2.5 font-semibold text-[#3D405B] hover:border-[#3D405B] transition-colors disabled:opacity-60">Cancel</button>
          <button data-testid="add-grade-save" onClick={submit} disabled={saving}
            className="flex-1 rounded-full bg-[#81B29A] text-white py-2.5 font-semibold hover:bg-[#6a9a83] transition-colors disabled:opacity-60">{saving ? "Saving…" : "Add to gradebook"}</button>
        </div>
      </div>
    </div>
  );
}

function Status({ s }) {
  const map = { Graded: "#81B29A", "Needs Review": "#F4A261", Submitted: "#A8DADC", "In Progress": "#457B9D" };
  return <span className="text-xs px-2.5 py-1 rounded-full text-white" style={{ background: map[s] || "#6B7280" }}>{s}</span>;
}

function SubmissionDrawer({ sub, onClose, onUpdated, onReset }) {
  const [pts, setPts] = useState(sub.points_earned);
  const [comment, setComment] = useState(sub.teacher_comments || "");
  const [saving, setSaving] = useState(false);
  const [pages, setPages] = useState([]);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    api.get(`/assignments/${sub.assignment_id}`).then((r) => setPages(r.data.original_images || [])).catch(() => {});
  }, [sub.assignment_id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/submissions/${sub.id}/override`, { points_earned: Number(pts), teacher_comments: comment });
      toast.success("Grade updated.");
      onUpdated(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-[#3D405B]/40" onClick={onClose} />
      <div data-testid="submission-drawer" className="relative w-full max-w-lg bg-[#F7F5F0] h-full overflow-y-auto p-8 shadow-xl">
        <button onClick={onClose} className="absolute top-6 right-6 text-[#6B7280] hover:text-[#3D405B]"><X /></button>
        <h2 className="font-head text-2xl font-bold text-[#3D405B]">{sub.assignment_title}</h2>
        <p className="text-[#6B7280] text-sm">{sub.subject} · <span className="stat">{sub.percentage}% ({sub.letter_grade})</span></p>

        {pages.length > 0 && (
          <div className="mt-5">
            <p className="text-sm font-semibold text-[#3D405B] flex items-center gap-2 mb-2"><ImageIcon size={15} className="text-[#E07A5F]" /> Original textbook pages</p>
            <div className="flex gap-2 flex-wrap">
              {pages.map((p, i) => (
                <button key={p.id || i} onClick={() => setZoom(p.path)}
                  data-testid={`page-thumb-${i}`} className="block w-20 h-24 rounded-lg overflow-hidden border border-[#EAE6DF] hover:ring-2 hover:ring-[#E07A5F]">
                  <AuthImage path={p.path} alt={`Page ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
        {zoom && <ImageZoomModal path={zoom} onClose={() => setZoom(null)} />}

        <div className="mt-6 space-y-4">
          {(sub.graded || []).map((g, i) => (
            <div key={i} className="rounded-xl bg-white border border-[#EAE6DF] p-4">
              <div className="flex justify-between items-start gap-3">
                <p className="text-sm font-medium text-[#3D405B]">{i + 1}. {g.question}</p>
                <span className="stat text-sm text-[#81B29A] whitespace-nowrap">{g.score}/{g.max_points}</span>
              </div>
              <p className="mt-2 text-sm text-[#6B7280]"><b>Answer:</b> {g.student_answer || "(blank)"}</p>
              {g.feedback && <p className="mt-1 text-sm text-[#3D405B] bg-[#F2EFE9] rounded-lg p-2">💬 {g.feedback}</p>}
              {g.teacher_review_required && <p className="mt-1 text-xs text-[#F4A261] font-medium">Flagged for review</p>}
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-xl bg-white border border-[#EAE6DF] p-5">
          <p className="font-head font-semibold text-[#3D405B] flex items-center gap-2 mb-3"><Pencil size={16} /> Override grade</p>
          <div className="flex items-center gap-3">
            <input data-testid="override-points" type="number" value={pts} onChange={(e) => setPts(e.target.value)}
              className="w-28 rounded-xl border border-[#EAE6DF] px-3 py-2 outline-none focus:ring-2 focus:ring-[#E07A5F]" />
            <span className="text-[#6B7280] text-sm">/ {sub.max_points} points</span>
          </div>
          <textarea data-testid="override-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Teacher comments…"
            className="mt-3 w-full min-h-[70px] rounded-xl border border-[#EAE6DF] px-3 py-2 outline-none focus:ring-2 focus:ring-[#E07A5F]" />
          <button data-testid="save-override-btn" onClick={save} disabled={saving}
            className="mt-3 w-full rounded-full bg-[#E07A5F] text-white py-2.5 font-semibold hover:bg-[#c96851] transition-colors disabled:opacity-60">
            {saving ? "Saving…" : "Save grade"}
          </button>
        </div>

        <button data-testid="drawer-reset-btn" onClick={() => onReset && onReset(sub)}
          className="mt-4 w-full flex items-center justify-center gap-2 rounded-full border border-[#EAE6DF] py-2.5 font-semibold text-[#6B7280] hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors">
          <RotateCcw size={16} /> Reset assignment & remove grade
        </button>
      </div>
    </div>
  );
}
